import React, { useState, useEffect, useCallback } from 'react';
import {
  ClipboardDocumentCheckIcon,
  EyeIcon,
  PencilSquareIcon,
  ChevronRightIcon,
  XMarkIcon,
  PlusIcon,
  SparklesIcon,
  TrashIcon,
  PaperAirplaneIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { config } from '../config/environment';
import { endpoints } from '../config/endpoints';
import { Spinner } from './ui';
import QuestionChart from './QuestionChart';
import type {
  Questionnaire, Question, QuestionAnswer, QuestionnaireResponsesData,
  QuestionWithData, GeneratedQuestion,
} from '../services/pracmagicTypes';

interface Props {
  clientId: string;
  coachClientId?: string;
  isCoach?: boolean;
  onCountChange?: (count: number) => void;
}

type QuestionType = 'rating' | 'yesno' | 'freetext' | 'number';
type Frequency = 'weekly' | 'biweekly' | 'monthly' | 'once';

const FREQ_LABELS: Record<string, string> = {
  weekly: 'Weekly', biweekly: 'Bi-weekly', monthly: 'Monthly', once: 'One-time',
};

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
});

const todayISO = () => new Date().toISOString().split('T')[0];

// ─── Merged data shape ────────────────────────────────────────────────────────

interface Instance {
  type: 'pending' | 'submitted';
  key: string;
  date: string;
  questionnaire: Questionnaire;
}

interface CheckInEntry {
  questionnaireId: string;
  title: string;
  frequency: string;
  questionnaire: Questionnaire;
  instances: Instance[];
  responseData: QuestionnaireResponsesData | null;
}

// ─── Modal wrapper ────────────────────────────────────────────────────────────

const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
    <div className="relative z-10 w-full max-w-lg bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl max-h-[90vh] flex flex-col">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700 shrink-0">
        <p className="text-sm font-semibold text-gray-100">{title}</p>
        <button onClick={onClose} className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors">
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>
      <div className="overflow-y-auto flex-1">{children}</div>
    </div>
  </div>
);

// ─── Inputs ───────────────────────────────────────────────────────────────────

const RatingInput = ({ value, onChange }: { value?: number; onChange: (v: number) => void }) => (
  <div className="flex gap-2 mt-1">
    {[1, 2, 3, 4, 5].map(n => (
      <button key={n} type="button" onClick={() => onChange(n)}
        className={`w-9 h-9 rounded-full border-2 text-sm font-semibold transition-colors ${
          value === n ? 'border-primary-500 bg-primary-600 text-white' : 'border-slate-600 text-gray-400 hover:border-primary-400 hover:text-primary-400'
        }`}>
        {n}
      </button>
    ))}
  </div>
);

const YesNoInput = ({ value, onChange }: { value?: boolean; onChange: (v: boolean) => void }) => (
  <div className="flex gap-2 mt-1">
    {([true, false] as const).map(v => (
      <button key={String(v)} type="button" onClick={() => onChange(v)}
        className={`px-5 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
          value === v ? 'border-primary-500 bg-primary-600 text-white' : 'border-slate-600 text-gray-300 hover:border-primary-400 hover:text-primary-300'
        }`}>
        {v ? 'Yes' : 'No'}
      </button>
    ))}
  </div>
);

// ─── Answer form ──────────────────────────────────────────────────────────────

const AnswerForm: React.FC<{
  questionnaire: Questionnaire;
  onSubmitted: () => void;
  backfill?: boolean;
}> = ({ questionnaire, onSubmitted, backfill = false }) => {
  const questions = questionnaire.questions || [];
  const [answers, setAnswers] = useState<Record<string, QuestionAnswer>>({});
  const [responseDate, setResponseDate] = useState(todayISO());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const setAnswer = (qid: string, partial: Partial<QuestionAnswer>) =>
    setAnswers(prev => ({ ...prev, [qid]: { ...prev[qid], question_id: qid, ...partial } }));

  const canSubmit = questions.every(q => {
    const a = answers[q.id];
    if (!a) return false;
    if (q.type === 'rating')   return a.rating_value !== undefined;
    if (q.type === 'number')   return a.rating_value !== undefined && a.rating_value >= 0;
    if (q.type === 'yesno')    return a.bool_value !== undefined;
    if (q.type === 'freetext') return !!(a.text_value?.trim());
    return false;
  });

  const submit = async () => {
    setSubmitting(true); setError('');
    try {
      const res = await fetch(
        `${config.apiBaseUrl}${endpoints.main.questionnaires.respond(questionnaire.id)}`,
        { method: 'POST', headers: authHeaders(), body: JSON.stringify({ answers: Object.values(answers), response_date: responseDate, backfill }) }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Submission failed');
      onSubmitted();
    } catch (e: any) {
      setError(e.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="px-5 py-4 space-y-4">
      {questions.map((q: Question, i: number) => (
        <div key={q.id}>
          <p className="text-sm font-medium text-gray-200">
            <span className="text-gray-500 mr-1">{i + 1}.</span> {q.text}
          </p>
          {q.type === 'rating' && (
            <>
              <RatingInput value={answers[q.id]?.rating_value} onChange={v => setAnswer(q.id, { rating_value: v })} />
              <p className="text-xs text-gray-500 mt-1">1 = not at all · 5 = very much</p>
            </>
          )}
          {q.type === 'number' && (
            <input type="number" min={0} value={answers[q.id]?.rating_value ?? ''}
              onChange={e => { const v = parseInt(e.target.value, 10); setAnswer(q.id, { rating_value: isNaN(v) ? undefined : v }); }}
              className="mt-1 w-24 text-sm border border-slate-600 rounded-lg px-3 py-2 text-gray-200 bg-slate-800 focus:ring-2 focus:ring-primary-500"
              placeholder="0" />
          )}
          {q.type === 'yesno' && <YesNoInput value={answers[q.id]?.bool_value} onChange={v => setAnswer(q.id, { bool_value: v })} />}
          {q.type === 'freetext' && (
            <textarea rows={3} value={answers[q.id]?.text_value || ''}
              onChange={e => setAnswer(q.id, { text_value: e.target.value })}
              className="w-full mt-1 text-sm border border-slate-600 rounded-lg px-3 py-2 text-gray-200 bg-slate-800 focus:ring-2 focus:ring-primary-500 resize-none"
              placeholder="Your answer…" />
          )}
        </div>
      ))}
      <div>
        <label className="block text-xs font-medium text-gray-400 mb-1">As-Of Date</label>
        <input type="date" value={responseDate} max={todayISO()} onChange={e => setResponseDate(e.target.value)}
          className="text-sm border border-slate-600 rounded-lg px-3 py-2 text-gray-200 bg-slate-800 focus:ring-2 focus:ring-primary-500 [color-scheme:dark]" />
      </div>
      {error && <p className="text-xs text-red-400">{error}</p>}
      <button onClick={submit} disabled={!canSubmit || submitting}
        className="w-full py-2.5 text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white rounded-lg disabled:opacity-40 transition-colors">
        {submitting ? 'Submitting…' : 'Submit Check-in'}
      </button>
    </div>
  );
};

// ─── Response viewer ──────────────────────────────────────────────────────────

const ResponseViewer: React.FC<{ questions: QuestionWithData[]; submittedAt: string }> = ({ questions, submittedAt }) => (
  <div className="px-5 py-4 space-y-4">
    <p className="text-xs text-slate-400">
      Submitted {new Date(submittedAt).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
    </p>
    {questions.map(q => {
      const dp = q.data_points.find(d => d.submitted_at === submittedAt);
      if (!dp) return null;
      return (
        <div key={q.id} className="border-b border-slate-700/60 pb-3 last:border-0 last:pb-0">
          <p className="text-xs text-gray-400 mb-1">{q.text}</p>
          <p className="text-sm font-medium text-gray-100">
            {q.type === 'rating'   && `${dp.rating_value} / 5`}
            {q.type === 'yesno'   && (dp.bool_value ? 'Yes' : 'No')}
            {q.type === 'freetext' && dp.text_value}
            {q.type === 'number'  && dp.rating_value}
          </p>
        </div>
      );
    })}
  </div>
);

// ─── Check-in row ─────────────────────────────────────────────────────────────

const CheckInRow: React.FC<{
  entry: CheckInEntry;
  isLast: boolean;
  isCoach: boolean;
  onRefresh: () => void;
  onDelete?: (id: string) => void;
  onToggle?: (q: Questionnaire) => void;
  onRemind?: (id: string) => void;
}> = ({ entry, isLast, isCoach, onRefresh, onDelete, onToggle, onRemind }) => {
  const [open, setOpen]   = useState(false);
  const [modal, setModal] = useState<{ instance: Instance } | null>(null);

  const { instances, responseData } = entry;
  const submittedCount = instances.filter(i => i.type === 'submitted').length;
  const pendingCount   = instances.filter(i => i.type === 'pending').length;
  const lastSubmitted  = instances.filter(i => i.type === 'submitted').sort((a, b) => b.date.localeCompare(a.date))[0];

  const hasChartData = responseData
    ? responseData.questions.some(q => (q.type === 'rating' || q.type === 'yesno' || q.type === 'number') && q.data_points.length > 1)
    : false;

  const formatDate = (d: string) => new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

  const getDueBadge = (inst: Instance) => {
    if (inst.type !== 'pending') return null;
    const today = new Date(); today.setHours(0,0,0,0);
    const due   = new Date(inst.date + 'T00:00:00'); due.setHours(0,0,0,0);
    const diff  = Math.round((due.getTime() - today.getTime()) / 86400000);
    const label = due.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    if (diff < 0)  return <span className="text-xs text-red-400 bg-red-900/30 px-2 py-0.5 rounded-full">Overdue</span>;
    if (diff === 0) return <span className="text-xs text-amber-400 bg-amber-900/30 px-2 py-0.5 rounded-full">Today</span>;
    return <span className="text-xs text-gray-400 bg-slate-700/60 px-2 py-0.5 rounded-full">Next Due: {label}</span>;
  };

  return (
    <>
      <div className={isLast ? '' : 'border-b border-slate-700/60'}>
        {/* Row header */}
        <button type="button" onClick={() => setOpen(o => !o)}
          className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-slate-700/30 transition-colors">
          <ChevronRightIcon className={`w-3.5 h-3.5 text-slate-500 flex-shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-200">{entry.title}</span>
              {pendingCount > 0 && !isCoach && (
                <span className="text-xs font-medium text-primary-400 bg-primary-900/30 px-2 py-0.5 rounded-full">{pendingCount} pending</span>
              )}
              {isCoach && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${entry.questionnaire.is_active ? 'text-green-400 bg-green-900/30' : 'text-gray-500 bg-slate-700/60'}`}>
                  {entry.questionnaire.is_active ? 'Active' : 'Paused'}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500 flex-wrap">
              <span>{FREQ_LABELS[entry.frequency] || entry.frequency || 'One-time'}</span>
              {submittedCount > 0 && <span>{submittedCount} response{submittedCount !== 1 ? 's' : ''}</span>}
              {lastSubmitted && <span>Last: {formatDate(lastSubmitted.date)}</span>}
              {isCoach && entry.questionnaire.next_due_date && <span>Next due: {entry.questionnaire.next_due_date}</span>}
            </div>
          </div>
          {/* Coach per-item actions */}
          {isCoach && (
            <div className="flex items-center gap-2 flex-shrink-0 ml-2" onClick={e => e.stopPropagation()}>
              <button onClick={() => onToggle?.(entry.questionnaire)} title={entry.questionnaire.is_active ? 'Pause' : 'Activate'}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${entry.questionnaire.is_active ? 'bg-primary-600' : 'bg-slate-600'}`}>
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${entry.questionnaire.is_active ? 'translate-x-4' : 'translate-x-1'}`} />
              </button>
              <button onClick={() => onRemind?.(entry.questionnaireId)} title="Send reminder"
                className="text-slate-400 hover:text-primary-400 transition-colors p-1">
                <PaperAirplaneIcon className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => onDelete?.(entry.questionnaireId)} title="Delete"
                className="text-slate-400 hover:text-red-400 transition-colors p-1">
                <TrashIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </button>

        {/* Expanded */}
        {open && (
          <div>
            {hasChartData && (
              <div className="border-t border-slate-700/60 px-5 py-4 space-y-4">
                {responseData!.questions
                  .filter(q => (q.type === 'rating' || q.type === 'yesno' || q.type === 'number') && q.data_points.length > 1)
                  .map(q => (
                    <div key={q.id}>
                      <p className="text-xs text-slate-400 mb-2">{q.text}</p>
                      <QuestionChart q={q} />
                    </div>
                  ))}
              </div>
            )}

            <div className={`${hasChartData ? 'border-t border-slate-700/60 ' : ''}divide-y divide-slate-700/40`}>
              {instances.length === 0 ? (
                <p className="px-5 py-3 text-xs text-gray-500">No responses yet.</p>
              ) : instances.map(inst => (
                <div key={inst.key} className="flex items-center gap-3 px-5 py-2.5 hover:bg-slate-700/20 transition-colors">
                  <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-gray-300">{formatDate(inst.date)}</span>
                    {getDueBadge(inst)}
                    {inst.type === 'submitted' && <span className="text-xs text-green-400/80">Submitted</span>}
                  </div>
                  <button type="button" onClick={() => setModal({ instance: inst })}
                    title={inst.type === 'pending' ? 'Fill check-in' : 'View response'}
                    className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${
                      inst.type === 'pending'
                        ? 'text-primary-400 hover:text-primary-300 hover:bg-primary-900/20'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50'
                    }`}>
                    {inst.type === 'pending' ? <PencilSquareIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {modal && (
        <Modal
          title={modal.instance.type === 'pending' ? entry.title : `${entry.title} — ${formatDate(modal.instance.date)}`}
          onClose={() => setModal(null)}
        >
          {modal.instance.type === 'pending' ? (
            <AnswerForm questionnaire={modal.instance.questionnaire} onSubmitted={() => { setModal(null); onRefresh(); }} />
          ) : (
            responseData && <ResponseViewer questions={responseData.questions} submittedAt={modal.instance.date} />
          )}
        </Modal>
      )}
    </>
  );
};

// ─── Questionnaire builder modal ──────────────────────────────────────────────

const QuestionnaireBuilder: React.FC<{
  clientId: string;
  coachClientId?: string;
  onSaved: () => void;
  onClose: () => void;
}> = ({ clientId: _clientId, coachClientId, onSaved, onClose }) => {
  const [title, setTitle]           = useState('');
  const [frequency, setFrequency]   = useState<Frequency>('weekly');
  const [occurrences, setOccurrences] = useState('');
  const [startDate, setStartDate]   = useState(todayISO());
  const [questions, setQuestions]   = useState<{ text: string; type: QuestionType }[]>([]);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState('');
  const [saving, setSaving]         = useState(false);
  const [saveError, setSaveError]   = useState('');

  const generate = async () => {
    if (!coachClientId) { setGenerateError('Coach–client relationship ID not available.'); return; }
    setGenerating(true); setGenerateError('');
    try {
      const res = await fetch(`${config.apiBaseUrl}${endpoints.main.questionnaires.generate}`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ coach_client_id: Number(coachClientId) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Generation failed');
      if (data.questions?.length) {
        setQuestions(data.questions.map((q: GeneratedQuestion) => ({ text: q.text, type: q.type })));
        if (!title) setTitle('Weekly Check-in');
      } else {
        setGenerateError(data.message || 'No questions returned — add them manually.');
      }
    } catch (e: any) {
      setGenerateError(e.message || 'Failed to generate questions');
    } finally {
      setGenerating(false);
    }
  };

  const addQuestion = () => {
    if (questions.length >= 10) return;
    setQuestions(q => [...q, { text: '', type: 'rating' }]);
  };

  const save = async () => {
    if (!title.trim()) { setSaveError('Please enter a title'); return; }
    if (frequency !== 'once' && (!occurrences || Number(occurrences) < 1)) { setSaveError('Please enter the number of occurrences'); return; }
    if (questions.length === 0) { setSaveError('Add at least one question'); return; }
    if (questions.some(q => !q.text.trim())) { setSaveError('All question texts must be filled in'); return; }
    setSaving(true); setSaveError('');
    try {
      const res = await fetch(`${config.apiBaseUrl}${endpoints.main.questionnaires.create}`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          coach_client_id: coachClientId ? Number(coachClientId) : undefined,
          title: title.trim(), frequency,
          next_due_date: startDate,
          occurrences: frequency !== 'once' && occurrences !== '' ? Number(occurrences) : undefined,
          questions: questions.map((q, i) => ({ text: q.text, type: q.type, order_index: i })),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed');
      onSaved();
    } catch (e: any) {
      setSaveError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700 shrink-0">
          <h3 className="text-sm font-semibold text-gray-100">New Check-in</h3>
          <button onClick={onClose} className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors">
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Title</label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Weekly Check-in"
              className="w-full text-sm border border-slate-600 rounded-lg px-3 py-2 bg-slate-800 text-gray-200 focus:ring-2 focus:ring-primary-500" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Frequency</label>
              <select value={frequency} onChange={e => setFrequency(e.target.value as Frequency)}
                className="w-full text-sm border border-slate-600 rounded-lg px-3 py-2 bg-slate-800 text-gray-200 focus:ring-2 focus:ring-primary-500">
                {Object.entries(FREQ_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">First due date</label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                className="w-full text-sm border border-slate-600 rounded-lg px-3 py-2 bg-slate-800 text-gray-200 focus:ring-2 focus:ring-primary-500 [color-scheme:dark]" />
            </div>
            {frequency !== 'once' && (
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Occurrences <span className="text-red-400">*</span></label>
                <input type="number" min={1} max={12} value={occurrences} onChange={e => setOccurrences(e.target.value)} placeholder="Required"
                  className="w-full text-sm border border-slate-600 rounded-lg px-3 py-2 bg-slate-800 text-gray-200 focus:ring-2 focus:ring-primary-500" />
              </div>
            )}
          </div>

          <div className="border border-dashed border-slate-600 rounded-lg p-4">
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm font-medium text-gray-300">Questions</p>
              <button onClick={generate} disabled={generating}
                className="flex items-center gap-1.5 text-xs text-primary-400 hover:text-primary-300 font-medium disabled:opacity-50">
                <SparklesIcon className="w-3.5 h-3.5" />
                {generating ? 'Generating…' : 'Generate from Session Notes'}
              </button>
            </div>
            {generateError && <p className="text-xs text-amber-400 mt-1">{generateError}</p>}
            <p className="text-xs text-gray-500">Or add questions manually. Max 10.</p>
          </div>

          {questions.map((q, i) => (
            <div key={i} className="flex gap-2 items-start">
              <div className="flex flex-col gap-0.5 pt-2 shrink-0">
                <button onClick={() => setQuestions(qs => { const a=[...qs]; if(i>0){[a[i-1],a[i]]=[a[i],a[i-1]];} return a; })}
                  disabled={i===0} className="text-slate-600 hover:text-slate-400 disabled:opacity-0">
                  <ChevronUpIcon className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => setQuestions(qs => { const a=[...qs]; if(i<qs.length-1){[a[i],a[i+1]]=[a[i+1],a[i]];} return a; })}
                  disabled={i===questions.length-1} className="text-slate-600 hover:text-slate-400 disabled:opacity-0">
                  <ChevronDownIcon className="w-3.5 h-3.5" />
                </button>
              </div>
              <textarea rows={2} value={q.text}
                onChange={e => setQuestions(qs => qs.map((item, idx) => idx===i ? {...item, text: e.target.value} : item))}
                placeholder={`Question ${i+1}`}
                className="flex-1 text-sm border border-slate-600 rounded-lg px-3 py-2 bg-slate-800 text-gray-200 focus:ring-2 focus:ring-primary-500 resize-none" />
              <select value={q.type}
                onChange={e => setQuestions(qs => qs.map((item, idx) => idx===i ? {...item, type: e.target.value as QuestionType} : item))}
                className="text-xs border border-slate-600 rounded-lg px-2 py-2 bg-slate-800 text-gray-200 focus:ring-2 focus:ring-primary-500 shrink-0">
                <option value="rating">1–5 Rating</option>
                <option value="yesno">Yes / No</option>
                <option value="freetext">Free text</option>
                <option value="number">Number</option>
              </select>
              <button onClick={() => setQuestions(qs => qs.filter((_,idx) => idx!==i))}
                className="text-slate-500 hover:text-red-400 pt-2 shrink-0">
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          ))}

          <button onClick={addQuestion} disabled={questions.length >= 10}
            className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200 disabled:opacity-40">
            <PlusIcon className="w-4 h-4" />
            Add question{questions.length > 0 ? ` (${questions.length}/10)` : ''}
          </button>
        </div>

        <div className="px-5 py-4 border-t border-slate-700 flex items-center justify-between shrink-0">
          {saveError ? <p className="text-xs text-red-400">{saveError}</p> : <span />}
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-slate-600 rounded-lg text-gray-300 hover:bg-slate-800">Cancel</button>
            <button onClick={save} disabled={saving}
              className="px-4 py-2 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded-lg disabled:opacity-50">
              {saving ? 'Saving…' : 'Save & Activate'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const CheckInsTab: React.FC<Props> = ({ clientId, coachClientId, isCoach = false, onCountChange }) => {
  const [entries, setEntries]     = useState<CheckInEntry[]>([]);
  const [loading, setLoading]     = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (isCoach) {
        // Coach: single endpoint returns all questionnaires; responses via coach endpoint
        const listRes = await fetch(
          `${config.apiBaseUrl}${endpoints.main.questionnaires.byClient(clientId)}`,
          { headers: authHeaders() }
        );
        const listData = listRes.ok ? await listRes.json() : { questionnaires: [] };
        const qs: Questionnaire[] = (listData.questionnaires || []).filter((q: any) => q.source !== 'form');

        const responseResults = await Promise.all(
          qs.map(async q => {
            try {
              const res = await fetch(`${config.apiBaseUrl}${endpoints.main.questionnaires.responses(q.id)}`, { headers: authHeaders() });
              return res.ok ? await res.json() as QuestionnaireResponsesData : null;
            } catch { return null; }
          })
        );

        const built: CheckInEntry[] = qs.map((q, i) => {
          const responseData = responseResults[i];
          const dates = responseData
            ? Array.from(new Set(responseData.questions.flatMap(rq => rq.data_points.map(dp => dp.submitted_at)))).sort()
            : [];
          return {
            questionnaireId: q.id,
            title: q.title,
            frequency: q.frequency,
            questionnaire: q,
            instances: dates.map(date => ({ type: 'submitted' as const, key: `s-${q.id}-${date}`, date, questionnaire: q })),
            responseData,
          };
        });

        setEntries(built.sort((a, b) => a.title.localeCompare(b.title)));
      } else {
        // Client: pending + history endpoints
        const [pendingRes, historyRes] = await Promise.all([
          fetch(`${config.apiBaseUrl}${endpoints.main.questionnaires.assigned(clientId)}`, { headers: authHeaders() }),
          fetch(`${config.apiBaseUrl}${endpoints.main.questionnaires.completedByClient(clientId)}`, { headers: authHeaders() }),
        ]);
        const pendingData = pendingRes.ok ? await pendingRes.json() : { questionnaires: [] };
        const historyData = historyRes.ok ? await historyRes.json() : { questionnaires: [] };
        const pendingQs: any[] = (pendingData.questionnaires || []).filter((q: any) => q.source !== 'form');
        const historyQs: Questionnaire[] = (historyData.questionnaires || []).filter((q: any) => q.source !== 'form');

        const responseResults = await Promise.all(
          historyQs.map(async q => {
            try {
              const res = await fetch(`${config.apiBaseUrl}${endpoints.main.questionnaires.myResponses(q.id)}`, { headers: authHeaders() });
              return res.ok ? await res.json() as QuestionnaireResponsesData : null;
            } catch { return null; }
          })
        );
        const responseMap = new Map<string, QuestionnaireResponsesData>();
        responseResults.forEach(r => { if (r) responseMap.set(r.questionnaire.id, r); });

        const dueNow = pendingQs.filter(q => q.action_due_date && new Date(q.action_due_date + 'T00:00:00') <= new Date()).length;
        onCountChange?.(dueNow);

        const entryMap = new Map<string, CheckInEntry>();

        historyQs.forEach(q => {
          const rd = responseMap.get(q.id) ?? null;
          const dates = rd
            ? Array.from(new Set(rd.questions.flatMap(rq => rq.data_points.map(dp => dp.submitted_at)))).sort()
            : [];
          entryMap.set(q.id, {
            questionnaireId: q.id, title: q.title, frequency: q.frequency, questionnaire: q,
            instances: dates.map(date => ({ type: 'submitted' as const, key: `s-${q.id}-${date}`, date, questionnaire: q })),
            responseData: rd,
          });
        });

        pendingQs.forEach(q => {
          const dueDate = q.action_due_date || q.next_due_date || todayISO();
          const inst: Instance = { type: 'pending', key: q.action_item_id || `p-${q.id}-${dueDate}`, date: dueDate, questionnaire: q };
          if (entryMap.has(q.id)) {
            const ex = entryMap.get(q.id)!;
            entryMap.set(q.id, { ...ex, questionnaire: q, instances: [...ex.instances, inst] });
          } else {
            entryMap.set(q.id, { questionnaireId: q.id, title: q.title, frequency: q.frequency, questionnaire: q, instances: [inst], responseData: null });
          }
        });

        const sorted = Array.from(entryMap.values()).sort((a, b) => {
          const ap = a.instances.some(i => i.type === 'pending') ? 0 : 1;
          const bp = b.instances.some(i => i.type === 'pending') ? 0 : 1;
          return ap - bp || a.title.localeCompare(b.title);
        });
        setEntries(sorted);
      }
    } finally {
      setLoading(false);
    }
  }, [clientId, isCoach, onCountChange]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this check-in and all responses?')) return;
    await fetch(`${config.apiBaseUrl}${endpoints.main.questionnaires.delete(id)}`, { method: 'DELETE', headers: authHeaders() });
    load();
  };

  const handleToggle = async (q: Questionnaire) => {
    await fetch(`${config.apiBaseUrl}${endpoints.main.questionnaires.update(q.id)}`, {
      method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ is_active: !q.is_active }),
    });
    load();
  };

  const handleRemind = async (id: string) => {
    await fetch(`${config.apiBaseUrl}${endpoints.main.questionnaires.sendReminder(id)}`, { method: 'POST', headers: authHeaders() });
    alert('Reminder sent!');
  };

  if (loading) return <Spinner size="md" className="py-12" />;

  return (
    <div className="space-y-4">
      {/* Header row with create button */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-400">{entries.length} check-in{entries.length !== 1 ? 's' : ''}</span>
        <button onClick={() => setShowBuilder(true)}
          className="flex items-center gap-1.5 text-sm bg-primary-600 hover:bg-primary-700 text-white px-3 py-1.5 rounded-lg font-medium transition-colors">
          <PlusIcon className="w-4 h-4" /> New Check-in
        </button>
      </div>

      {entries.length === 0 ? (
        <div className="bg-slate-800 rounded-xl border border-slate-700 px-6 py-8 flex flex-col items-center text-center">
          <ClipboardDocumentCheckIcon className="w-9 h-9 text-slate-500 mb-2" />
          <p className="text-sm font-semibold text-gray-300">No check-ins yet</p>
          <p className="text-sm text-gray-500 mt-1 max-w-xs">
            {isCoach ? 'Create one to track this client\'s progress.' : 'Your coach will assign check-ins when it\'s time.'}
          </p>
        </div>
      ) : (
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          {entries.map((entry, idx) => (
            <CheckInRow
              key={entry.questionnaireId}
              entry={entry}
              isLast={idx === entries.length - 1}
              isCoach={isCoach}
              onRefresh={load}
              onDelete={isCoach ? handleDelete : undefined}
              onToggle={isCoach ? handleToggle : undefined}
              onRemind={isCoach ? handleRemind : undefined}
            />
          ))}
        </div>
      )}

      {showBuilder && (
        <QuestionnaireBuilder
          clientId={clientId}
          coachClientId={coachClientId}
          onSaved={() => { setShowBuilder(false); load(); }}
          onClose={() => setShowBuilder(false)}
        />
      )}
    </div>
  );
};

export default CheckInsTab;
